import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, apiClient } from "@/App";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { 
  Headphones, 
  Play,
  Plus,
  Search,
  Users,
  Activity,
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw,
  ArrowRight,
  Smartphone,
  Monitor,
  Stethoscope
} from "lucide-react";
import { toast } from "sonner";

export default function PTALiveTestPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  
  const [patients, setPatients] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [formData, setFormData] = useState({
    patient_name: "",
    patient_phone: "",
    patient_email: "",
    headphone_type: "generic",
    test_mode: "live_web",
    notes: ""
  });

  // Pre-select patient if passed in URL
  const preSelectedPatientId = searchParams.get("patient_id");

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (preSelectedPatientId && patients.length > 0) {
      const patient = patients.find(p => p.id === preSelectedPatientId);
      if (patient) {
        selectPatient(patient);
      }
    }
  }, [preSelectedPatientId, patients]);

  const fetchData = async () => {
    try {
      const [patientsRes, sessionsRes] = await Promise.all([
        apiClient.get("/patients"),
        apiClient.get("/pta-live-sessions")
      ]);
      setPatients(patientsRes.data);
      setSessions(sessionsRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const selectPatient = (patient) => {
    setSelectedPatient(patient);
    setFormData(prev => ({
      ...prev,
      patient_name: patient.name,
      patient_phone: patient.phone || "",
      patient_email: patient.email || ""
    }));
  };

  const createSession = async () => {
    if (!selectedPatient && !formData.patient_name) {
      toast.error("Please select a patient or enter patient details");
      return;
    }

    setCreating(true);
    try {
      const payload = {
        patient_id: selectedPatient?.id || `walk-in-${Date.now()}`,
        patient_name: formData.patient_name,
        patient_phone: formData.patient_phone,
        patient_email: formData.patient_email,
        clinic_id: user.clinic_id || "default",
        test_mode: formData.test_mode,
        headphone_type: formData.headphone_type,
        notes: formData.notes
      };

      const response = await apiClient.post("/pta-live-sessions", payload);
      toast.success("Test session created!");
      
      // Navigate to audiologist console
      navigate(`/pta-test/audiologist/${response.data.session_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create session");
    } finally {
      setCreating(false);
    }
  };

  const filteredPatients = patients.filter(p =>
    p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.phone?.includes(searchTerm) ||
    p.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const recentSessions = sessions.filter(s => !["completed", "cancelled"].includes(s.status)).slice(0, 20);
  const completedSessions = sessions.filter(s => s.status === "completed").slice(0, 20);
  const activeSessions = sessions.filter(s => 
    !["completed", "cancelled"].includes(s.status)
  );

  const formatStatus = (status) => {
    const statusMap = {
      created: { label: "Waiting", color: "bg-amber-100 text-amber-700" },
      patient_joined: { label: "Patient Joined", color: "bg-green-100 text-green-700" },
      instructions: { label: "Instructions", color: "bg-blue-100 text-blue-700" },
      calibration: { label: "Calibrating", color: "bg-purple-100 text-purple-700" },
      testing_left: { label: "Testing L", color: "bg-sky-100 text-sky-700" },
      testing_right: { label: "Testing R", color: "bg-red-100 text-red-700" },
      completed: { label: "Completed", color: "bg-emerald-100 text-emerald-700" },
      cancelled: { label: "Cancelled", color: "bg-slate-100 text-slate-700" }
    };
    return statusMap[status] || { label: status, color: "bg-slate-100 text-slate-700" };
  };

  const getSeverityColor = (severity) => {
    const colors = {
      "Normal": "text-emerald-600",
      "Mild": "text-amber-600",
      "Moderate": "text-orange-600",
      "Severe": "text-red-600",
      "Profound": "text-red-700"
    };
    return colors[severity] || "text-slate-600";
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="pta-live-test-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Headphones className="w-7 h-7 text-indigo-600" />
              Live PTA Testing
            </h1>
            <p className="text-slate-500">Conduct real-time hearing tests with two-device setup</p>
          </div>
          
          {activeSessions.length > 0 && (
            <Badge variant="outline" className="text-amber-600 border-amber-300">
              {activeSessions.length} Active Session(s)
            </Badge>
          )}
        </div>

        <Tabs defaultValue="new" className="space-y-6">
          <TabsList>
            <TabsTrigger value="new" data-testid="tab-new-test">
              <Plus className="w-4 h-4 mr-2" />
              New Test
            </TabsTrigger>
            <TabsTrigger value="sessions" data-testid="tab-sessions">
              <Activity className="w-4 h-4 mr-2" />
              Active Sessions
            </TabsTrigger>
            <TabsTrigger value="completed" data-testid="tab-completed">
              <CheckCircle className="w-4 h-4 mr-2" />
              Completed
            </TabsTrigger>
          </TabsList>

          {/* New Test Tab */}
          <TabsContent value="new">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Patient Selection */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Select Patient</CardTitle>
                  <CardDescription>Choose an existing patient or enter details for walk-in</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                    <Input
                      placeholder="Search patients by name, phone, or email..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>

                  <div className="max-h-64 overflow-y-auto border rounded-lg">
                    {filteredPatients.length === 0 ? (
                      <div className="p-4 text-center text-slate-500">
                        No patients found
                      </div>
                    ) : (
                      filteredPatients.slice(0, 20).map(patient => (
                        <div
                          key={patient.id}
                          className={`p-3 border-b last:border-0 cursor-pointer hover:bg-slate-50 transition-colors ${
                            selectedPatient?.id === patient.id ? 'bg-indigo-50 border-indigo-200' : ''
                          }`}
                          onClick={() => selectPatient(patient)}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-slate-900">{patient.name}</p>
                              <p className="text-sm text-slate-500">
                                {patient.phone || patient.email || "No contact info"}
                              </p>
                            </div>
                            {selectedPatient?.id === patient.id && (
                              <CheckCircle className="w-5 h-5 text-indigo-600" />
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  <div className="border-t pt-4">
                    <p className="text-sm text-slate-500 mb-2">Or enter walk-in patient details:</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Name *</Label>
                        <Input
                          value={formData.patient_name}
                          onChange={(e) => {
                            setFormData({...formData, patient_name: e.target.value});
                            setSelectedPatient(null);
                          }}
                          placeholder="Patient name"
                        />
                      </div>
                      <div>
                        <Label>Phone</Label>
                        <Input
                          value={formData.patient_phone}
                          onChange={(e) => setFormData({...formData, patient_phone: e.target.value})}
                          placeholder="+91-9876543210"
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Test Configuration */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Test Configuration</CardTitle>
                  <CardDescription>Set up the test parameters</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Test Mode</Label>
                    <Select 
                      value={formData.test_mode} 
                      onValueChange={(v) => setFormData({...formData, test_mode: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="live_web">
                          <div className="flex items-center gap-2">
                            <Smartphone className="w-4 h-4 text-indigo-500" />
                            Web-Based (Two Devices)
                          </div>
                        </SelectItem>
                        <SelectItem value="audiometer">
                          <div className="flex items-center gap-2">
                            <Stethoscope className="w-4 h-4 text-emerald-500" />
                            Connected Audiometer
                          </div>
                        </SelectItem>
                        <SelectItem value="manual">
                          <div className="flex items-center gap-2">
                            <Monitor className="w-4 h-4 text-slate-500" />
                            Manual Entry
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Headphone Type</Label>
                    <Select 
                      value={formData.headphone_type} 
                      onValueChange={(v) => setFormData({...formData, headphone_type: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="generic">Generic/Unknown</SelectItem>
                        <SelectItem value="hda300">Sennheiser HDA 300</SelectItem>
                        <SelectItem value="dd45">DD45 Insert</SelectItem>
                        <SelectItem value="tdh39">TDH-39 Supra-aural</SelectItem>
                        <SelectItem value="airpods">Apple AirPods</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-400 mt-1">
                      For clinical accuracy, use calibrated audiometric headphones
                    </p>
                  </div>

                  <div>
                    <Label>Notes (Optional)</Label>
                    <Input
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      placeholder="Any special instructions or notes..."
                    />
                  </div>

                  <div className="bg-blue-50 rounded-lg p-4 mt-4">
                    <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                      <Headphones className="w-4 h-4" />
                      How it works
                    </h4>
                    <ol className="text-sm text-blue-700 space-y-1">
                      <li>1. Create a test session below</li>
                      <li>2. Share the session code with the patient</li>
                      <li>3. Patient joins on their device</li>
                      <li>4. You control the test, patient responds</li>
                      <li>5. Results are automatically recorded</li>
                    </ol>
                  </div>

                  <Button 
                    onClick={createSession}
                    disabled={creating || (!selectedPatient && !formData.patient_name)}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 py-6 text-lg"
                    data-testid="start-test-btn"
                  >
                    {creating ? (
                      <>
                        <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                        Creating Session...
                      </>
                    ) : (
                      <>
                        <Play className="w-5 h-5 mr-2" />
                        Start Test Session
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Active Sessions Tab */}
          <TabsContent value="sessions">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Active Test Sessions</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8">
                    <RefreshCw className="w-8 h-8 animate-spin text-indigo-600 mx-auto" />
                  </div>
                ) : recentSessions.length === 0 ? (
                  <div className="text-center py-12">
                    <Activity className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No active test sessions</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {recentSessions.map(session => {
                      const statusInfo = formatStatus(session.status);
                      return (
                        <div 
                          key={session.id}
                          className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50 cursor-pointer"
                          data-testid={`session-row-${session.id}`}
                          onClick={() => {
                            if (session.status !== "cancelled") {
                              navigate(`/pta-test/audiologist/${session.id}`);
                            }
                          }}
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                              <Headphones className="w-5 h-5 text-indigo-600" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{session.patient_name}</p>
                              <div className="flex items-center gap-2 text-sm text-slate-500">
                                <Clock className="w-3 h-3" />
                                {new Date(session.created_at).toLocaleString()}
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3">
                            <Badge className={statusInfo.color}>
                              {statusInfo.label}
                            </Badge>
                            <ArrowRight className="w-5 h-5 text-slate-400" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Completed Sessions Tab */}
          <TabsContent value="completed">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Completed Tests</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8">
                    <RefreshCw className="w-8 h-8 animate-spin text-indigo-600 mx-auto" />
                  </div>
                ) : completedSessions.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No completed tests yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {completedSessions.map(session => (
                      <div 
                        key={session.id}
                        className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50 cursor-pointer"
                        data-testid={`completed-row-${session.id}`}
                        onClick={() => navigate(`/live-monitor?session=${session.id}`)}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                            <CheckCircle className="w-5 h-5 text-emerald-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{session.patient_name}</p>
                            <div className="flex items-center gap-2 text-sm text-slate-500">
                              <Clock className="w-3 h-3" />
                              {new Date(session.created_at).toLocaleString()}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          <div className="text-right text-sm">
                            <div className="flex gap-3">
                              <span className="text-red-600">R: {session.pta_right ?? "--"}dB</span>
                              <span className="text-blue-600">L: {session.pta_left ?? "--"}dB</span>
                            </div>
                            <div className="flex gap-2">
                              <span className={`text-xs ${getSeverityColor(session.severity_right)}`}>
                                {session.severity_right || "—"}
                              </span>
                              <span className={`text-xs ${getSeverityColor(session.severity_left)}`}>
                                {session.severity_left || "—"}
                              </span>
                            </div>
                          </div>
                          <Badge className="bg-emerald-100 text-emerald-700">Completed</Badge>
                          <ArrowRight className="w-5 h-5 text-slate-400" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
